import { useState, type FormEvent } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useAsyncAction } from "@/hooks/useAsyncAction";
import { api, type Adjunto, type Caja, type CambioMoneda, type LibroCaja, type LineaCaja, type SaldosCaja } from "@/lib/api";
import {
  aPayloadCambio,
  errorFiltros,
  filtrosIniciales,
  formCambio,
  lineasAcreditadoSinCaja,
  queryCaja,
  validarCambio,
  type ErroresCambio,
  type FiltrosCaja,
  type FormCambio,
} from "@/lib/caja";
import { cajaPorDefecto, hayVariasCajas, opcionesCaja } from "@/lib/cajas";
import { usePanelCajas } from "./usePanelCajas";

const SIN_ERRORES: ErroresCambio = {};
type Dialogo = { tipo: "form"; cambio: CambioMoneda | null } | { tipo: "borrar"; linea: LineaCaja } | null;

/**
 * Lógica de Caja (sin JSX): saldos, libro filtrable por período/moneda/tipo/caja, y alta/edición/baja de cambios de
 * moneda. Sólo el admin escribe. Editar pide antes el cambio completo (el libro no trae nota ni adjuntos). Las cajas y
 * las transferencias entre ellas, en `usePanelCajas`. La ven admin y contador.
 */
export function useCajaTab() {
  const { fid, membresia } = useContextoApp();
  const base = `/api/fideicomisos/${fid}/caja`;
  const esAdmin = membresia.rol === "admin";

  const saldos = useApiResource<SaldosCaja>(`${base}/saldos`);
  const [filtros, setFiltros] = useState<FiltrosCaja>(filtrosIniciales);
  const errorF = errorFiltros(filtros);
  const libro = useApiResource<LibroCaja>(errorF ? null : `${base}/movimientos${queryCaja(filtros)}`);
  const listaCajas = useApiResource<Caja[]>(`/api/fideicomisos/${fid}/cajas`);
  const cajas = listaCajas.data ?? [];

  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [form, setForm] = useState<FormCambio>(() => formCambio());
  const [mostrarErrores, setMostrarErrores] = useState(false);

  const accionGuardar = useAsyncAction((id: number | null, datos: ReturnType<typeof aPayloadCambio>) =>
    id === null ? api.post<CambioMoneda>(`${base}/cambios`, datos) : api.patch<CambioMoneda>(`${base}/cambios/${id}`, datos),
  );
  const accionBorrar = useAsyncAction((id: number) => api.delete<void>(`${base}/cambios/${id}`).then(() => true));
  const accionCargarCambio = useAsyncAction((id: number) => api.get<CambioMoneda>(`${base}/cambios/${id}`));
  const accionSubir = useAsyncAction((cambioId: number, archivo: File) => {
    const f = new FormData();
    f.append("archivo", archivo);
    return api.upload<Adjunto>(`${base}/cambios/${cambioId}/adjuntos`, f);
  });
  const accionBorrarAdjunto = useAsyncAction((adjuntoId: number) => api.delete<void>(`/api/fideicomisos/${fid}/adjuntos/${adjuntoId}`).then(() => true));

  const errores = validarCambio(form);
  const recargar = () => {
    saldos.recargar();
    libro.recargar();
    listaCajas.recargar();
  };
  const panel = usePanelCajas(fid, cajas, recargar);
  const cerrar = () => setDialogo(null);

  const abrirNuevo = () => {
    setForm(formCambio(undefined, new Date(), cajaPorDefecto(cajas)));
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    setDialogo({ tipo: "form", cambio: null });
  };

  const abrirEditar = async (linea: LineaCaja) => {
    if (!linea.referencia) return;
    accionCargarCambio.limpiarError();
    const c = await accionCargarCambio.run(linea.referencia.id);
    if (!c) return;
    setForm(formCambio(c));
    setMostrarErrores(false);
    accionGuardar.limpiarError();
    setDialogo({ tipo: "form", cambio: c });
  };

  const abrirBorrar = (linea: LineaCaja) => {
    accionBorrar.limpiarError();
    setDialogo({ tipo: "borrar", linea });
  };

  const guardar = async (e: FormEvent) => {
    e.preventDefault();
    setMostrarErrores(true);
    if (dialogo?.tipo !== "form" || Object.keys(errores).length > 0) return;
    if (await accionGuardar.run(dialogo.cambio?.id ?? null, aPayloadCambio(form))) {
      cerrar();
      recargar();
    }
  };

  const confirmarBorrar = async () => {
    if (dialogo?.tipo !== "borrar" || !dialogo.linea.referencia) return;
    if (await accionBorrar.run(dialogo.linea.referencia.id)) {
      cerrar();
      recargar();
    }
  };

  /** Refresca el cambio que está abierto en el diálogo (después de subir o borrar un comprobante), sin cerrarlo. */
  const recargarCambioAbierto = async (id: number) => {
    const actualizado = await api.get<CambioMoneda>(`${base}/cambios/${id}`);
    setDialogo({ tipo: "form", cambio: actualizado });
  };

  const subirAdjunto = async (archivo: File) => {
    if (dialogo?.tipo !== "form" || !dialogo.cambio) return;
    accionBorrarAdjunto.limpiarError();
    if (await accionSubir.run(dialogo.cambio.id, archivo)) await recargarCambioAbierto(dialogo.cambio.id);
  };

  const borrarAdjunto = async (a: Adjunto) => {
    if (dialogo?.tipo !== "form" || !dialogo.cambio) return;
    const id = dialogo.cambio.id;
    accionSubir.limpiarError();
    if (await accionBorrarAdjunto.run(a.id)) await recargarCambioAbierto(id);
  };

  return {
    esAdmin,
    cajas,
    variasCajas: hayVariasCajas(cajas),
    /** Columna y filtro de caja: con más de una caja (también si la otra está archivada: tiene historia). */
    mostrarCaja: cajas.length > 1,
    opcionesFiltroCaja: [{ valor: "", etiqueta: "Todas las cajas" }, ...cajas.map((c) => ({ valor: String(c.id), etiqueta: c.nombre }))],
    opcionesCajaCambio: opcionesCaja(cajas, dialogo?.tipo === "form" ? (dialogo.cambio?.caja_id ?? null) : null),
    panel,
    saldos: saldos.data ?? null,
    cargandoSaldos: saldos.loading && !saldos.data,
    libro: libro.data ?? null,
    acreditadoSinCaja: libro.data ? lineasAcreditadoSinCaja(libro.data.acreditado_sin_caja) : [],
    cargandoLibro: !errorF && libro.loading && !libro.data,
    errorCargaLibro: errorF || libro.data ? null : (libro.error?.message ?? null),
    filtros,
    cambiarFiltro: <K extends keyof FiltrosCaja>(campo: K, valor: FiltrosCaja[K]) => setFiltros((f) => ({ ...f, [campo]: valor })),
    errorFiltros: errorF,
    rutaDescarga: (a: Adjunto) => `/api/fideicomisos/${fid}/adjuntos/${a.id}/descarga`,
    dialogo,
    cerrar,
    abrirNuevo,
    abrirEditar,
    cargandoEditar: accionCargarCambio.loading,
    errorEditar: accionCargarCambio.error,
    abrirBorrar,
    form,
    cambiar: <K extends keyof FormCambio>(campo: K, valor: FormCambio[K]) => setForm((f) => ({ ...f, [campo]: valor })),
    errores: mostrarErrores ? errores : SIN_ERRORES,
    guardar,
    guardando: accionGuardar.loading,
    errorGuardar: accionGuardar.error,
    confirmarBorrar,
    borrando: accionBorrar.loading,
    errorBorrar: accionBorrar.error,
    subirAdjunto,
    borrarAdjunto,
    subiendoAdjunto: accionSubir.loading,
    errorAdjuntos: accionSubir.error ?? accionBorrarAdjunto.error,
  };
}
